import { ArrowLeft, Zap, CheckCircle2, AlertTriangle } from "lucide-react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Head } from "vite-react-ssg";
import { Card, CardContent } from "@/components/ui/card";
import RelatedLinks from "@/components/RelatedLinks";
import LastUpdated from "@/components/LastUpdated";
import { DEEPSEEK_PRICING, getPeakRate } from "@/lib/deepseek-pricing";

const DeepseekV41FlashGA = () => {
  const navigate = useNavigate();
  const canonical = "https://deepseek.ai/blog/deepseek-v41-flash-generally-available";
  const flash = DEEPSEEK_PRICING.models["v4-flash"];
  const pro = DEEPSEEK_PRICING.models["v4-pro"];
  const flashPeak = getPeakRate("v4-flash");
  const proPeak = getPeakRate("v4-pro");

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline:
      "DeepSeek-V4.1-Flash Is Generally Available: 552B MoE, Native Vision, New Peak/Off-Peak Pricing",
    datePublished: "2026-09-18T09:00:00+00:00",
    dateModified: "2026-09-18T09:00:00+00:00",
    author: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      url: "https://deepseek.ai",
    },
    publisher: {
      "@type": "Organization",
      name: "The Independent DeepSeek Guide",
      logo: { "@type": "ImageObject", url: "https://deepseek.ai/og-image.png" },
    },
    description:
      "DeepSeek released V4.1-Flash as a general-availability model on September 9, 2026: a 552B-parameter MoE with a causal encoder-decoder split, native image understanding, the API name deepseek-flash, retired V4-Flash aliases, V4-Pro routing from September 14 and a two-tier peak/off-peak rate card.",
    mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://deepseek.ai/" },
      { "@type": "ListItem", position: 2, name: "Blog", item: "https://deepseek.ai/blog" },
      { "@type": "ListItem", position: 3, name: "DeepSeek-V4.1-Flash GA", item: canonical },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      {
        "@type": "Question",
        name: "What is the API name for DeepSeek-V4.1-Flash?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "deepseek-flash. The base URL stays https://api.deepseek.com. The expiring beta ID deepseek-v4.1-flash-expires-on-0910 no longer serves traffic. The older names deepseek-v4-flash and deepseek-v4-flash-vision-exp are still accepted but those models are retired, so their requests are served by V4.1-Flash.",
        },
      },
      {
        "@type": "Question",
        name: "How much does DeepSeek-V4.1-Flash cost?",
        acceptedAnswer: {
          "@type": "Answer",
          text: `Per 1M tokens in USD, off-peak: $${flash.inputCacheMiss} input on a cache miss, $${flash.inputCacheHit} on a cache hit, $${flash.output} output. Peak rates are exactly double: $${flashPeak.inputCacheMiss}, $${flashPeak.inputCacheHit} and $${flashPeak.output}. Peak hours are Monday to Friday, 01:00-04:00 and 06:00-10:00 UTC; every other hour, including weekends, is off-peak. Rates last verified ${DEEPSEEK_PRICING.lastVerified}.`,
        },
      },
      {
        "@type": "Question",
        name: "Is the DeepSeek peak-hour surcharge finally active?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. From September 10, 2026 at 04:00 UTC the official rate card carries two tiers, with off-peak priced at 50% of peak. Until then DeepSeek billed a single flat rate, and this site reported the surcharge as announced-but-not-active, which was correct at the time.",
        },
      },
      {
        "@type": "Question",
        name: "What happens to DeepSeek-V4-Pro?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "V4-Pro is being phased out. Since September 14, 2026 at 04:00 UTC, deepseek-v4-pro requests route to V4.1-Flash and are billed at V4.1 rates until V4.1-Pro ships. Customers who ask DeepSeek to keep V4-Pro API service can continue on it with unchanged billing.",
        },
      },
      {
        "@type": "Question",
        name: "Does V4.1-Flash handle images?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "Yes. Image understanding is built into the model rather than bolted on through a separate vision variant, which is why DeepSeek-V4-Flash-Vision-Exp is retired alongside V4-Flash.",
        },
      },
      {
        "@type": "Question",
        name: "Are the V4.1-Flash weights open?",
        acceptedAnswer: {
          "@type": "Answer",
          text: "DeepSeek published the model on Hugging Face together with a technical report, in line with its previous releases. Licence terms are stated on the model card, not here — check it before commercial deployment.",
        },
      },
    ],
  };

  return (
    <>
      <Head>
        <title>DeepSeek-V4.1-Flash GA: New Prices, Peak Hours, Native Vision</title>
        <meta
          name="description"
          content="DeepSeek-V4.1-Flash went generally available on September 9, 2026 as deepseek-flash: 552B MoE, native image understanding, retired V4-Flash aliases, V4-Pro routed from September 14, and a live peak/off-peak rate card."
        />
        <link rel="canonical" href={canonical} />
        <meta name="robots" content="index, follow" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta
          property="og:title"
          content="DeepSeek-V4.1-Flash Is Generally Available — and Peak Pricing Goes Live"
        />
        <meta
          property="og:description"
          content="552B-parameter MoE with a causal encoder-decoder split and native vision, served as deepseek-flash. V4-Flash retired, V4-Pro routed from September 14, off-peak rates at half of peak."
        />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Head>

      <div className="min-h-screen bg-white">
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Button
            variant="ghost"
            onClick={() => navigate("/blog")}
            className="mb-6 text-slate-600 hover:text-[#0066FF]"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Blog
          </Button>

          <nav aria-label="Breadcrumb" className="text-xs text-slate-500 mb-4">
            <Link to="/" className="hover:text-[#0066FF]">Home</Link>
            <span className="mx-2">/</span>
            <Link to="/blog" className="hover:text-[#0066FF]">Blog</Link>
            <span className="mx-2">/</span>
            <span className="text-slate-700">DeepSeek-V4.1-Flash GA</span>
          </nav>

          <div className="mb-6 inline-flex items-center gap-2 text-xs font-medium bg-blue-50 text-[#0066FF] border border-blue-200 px-3 py-1.5 rounded-full">
            <Zap className="h-3.5 w-3.5" />
            API changelog · September 18, 2026 · Independent Guide
          </div>

          <article>
            <h1 className="text-3xl md:text-5xl font-bold text-slate-900 mb-4 leading-tight">
              DeepSeek AI Ships V4.1-Flash for Real: New Architecture, Native Vision, and Peak Pricing
              That Is Finally Live
            </h1>

            <p className="text-lg text-slate-600 mb-2">
              By the Deep Seek AI editorial desk · September 18, 2026 · 7 min read
            </p>
            <LastUpdated date="September 18, 2026" />
            <p className="text-base text-slate-500 mb-8 italic">
              Ten days ago the model existed only as an ID with an expiry date baked into it. On
              September 9, 2026 DeepSeek published the release notes: V4.1-Flash is a general
              availability model, it answers to <code>deepseek-flash</code>, and it comes with the
              first two-tier rate card DeepSeek has run on the V4 line.
            </p>

            <Card className="mb-8 border-blue-200 bg-blue-50/60">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold text-slate-900 mb-3 mt-0">The 60-second version</h2>
                <ul className="text-sm text-slate-800 space-y-2 list-disc pl-5">
                  <li>
                    <strong>DeepSeek-V4.1-Flash is GA</strong>, no longer a beta. API name:{" "}
                    <code>deepseek-flash</code>.
                  </li>
                  <li>
                    <strong>552B-parameter Mixture-of-Experts</strong> with a "Causal
                    Encoder–Decoder" split — roughly 8B active parameters on input, 16B on output.
                  </li>
                  <li>
                    <strong>Native image understanding</strong> inside the model, so the separate
                    vision variant is gone.
                  </li>
                  <li>
                    <strong>V4-Flash and V4-Flash-Vision-Exp are retired.</strong> Their model names
                    still resolve, but requests are served by V4.1-Flash.
                  </li>
                  <li>
                    <strong>V4-Pro is winding down:</strong> since September 14, 04:00 UTC,{" "}
                    <code>deepseek-v4-pro</code> traffic routes to V4.1-Flash at V4.1 rates until
                    V4.1-Pro arrives — unless you ask DeepSeek to keep V4-Pro service running.
                  </li>
                  <li>
                    <strong>Peak and off-peak pricing is live</strong> from September 10, 04:00 UTC.
                    Off-peak is half of peak.
                  </li>
                  <li>
                    <strong>Weights and a technical report are on Hugging Face</strong>, as with
                    previous DeepSeek releases.
                  </li>
                </ul>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What actually changed in the architecture
            </h2>
            <p className="text-slate-700 mb-4">
              The headline number — 552B total parameters — matters less than how few of them run per
              token. DeepSeek describes V4.1-Flash as a causal encoder–decoder MoE: the encoder path
              activates on the order of 8B parameters to read input, the decoder path around 16B to
              write output. That asymmetry is the point. Prompt-heavy workloads — long documents,
              retrieval context, codebases — are the cheap half of the model to run, and the expensive
              half only engages for the tokens you actually generate.
            </p>
            <p className="text-slate-700 mb-4">
              The second structural change is memory. DeepSeek reports the KV cache is{" "}
              <strong>4× smaller in HBM</strong> and <strong>8× smaller on SSD</strong> than the
              previous Flash generation. For anyone self-hosting the open weights that is the number
              to plan against: it decides how many concurrent long-context sessions fit on a given
              card before you start paging.
            </p>
            <p className="text-slate-700 mb-8">
              Vision is now native. Previously, image input on the Flash line meant a separate
              experimental model, <code>deepseek-v4-flash-vision-exp</code>. That model is retired
              because the capability moved inside. DeepSeek reports benchmark results above V4-Pro —
              its previous frontier tier — which is why Pro is being routed away rather than kept as
              the premium option.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              The new rate card, in full
            </h2>
            <p className="text-slate-700 mb-4">
              Prices below are per 1,000,000 tokens in USD, taken from DeepSeek's official pricing
              page and last verified by a human editor on {DEEPSEEK_PRICING.lastVerified}. Off-peak is
              the tier that applies most hours of the week, so we quote it first.
            </p>
            <div className="overflow-x-auto mb-4">
              <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden">
                <caption className="sr-only">
                  DeepSeek off-peak and peak API rates per 1M tokens after the V4.1-Flash GA release
                </caption>
                <thead className="bg-slate-100">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Model</th>
                    <th className="text-left px-4 py-3 font-semibold text-slate-700">Tier</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Input (miss)</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Input (hit)</th>
                    <th className="text-right px-4 py-3 font-semibold text-slate-700">Output</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="px-4 py-3 font-medium">{flash.label}</td>
                    <td className="px-4 py-3">Off-peak</td>
                    <td className="text-right px-4 py-3">${flash.inputCacheMiss}</td>
                    <td className="text-right px-4 py-3">${flash.inputCacheHit}</td>
                    <td className="text-right px-4 py-3">${flash.output}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">{flash.label}</td>
                    <td className="px-4 py-3">Peak</td>
                    <td className="text-right px-4 py-3">${flashPeak.inputCacheMiss}</td>
                    <td className="text-right px-4 py-3">${flashPeak.inputCacheHit}</td>
                    <td className="text-right px-4 py-3">${flashPeak.output}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">{pro.label}</td>
                    <td className="px-4 py-3">Off-peak</td>
                    <td className="text-right px-4 py-3">${pro.inputCacheMiss}</td>
                    <td className="text-right px-4 py-3">${pro.inputCacheHit}</td>
                    <td className="text-right px-4 py-3">${pro.output}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-medium">{pro.label}</td>
                    <td className="px-4 py-3">Peak</td>
                    <td className="text-right px-4 py-3">${proPeak.inputCacheMiss}</td>
                    <td className="text-right px-4 py-3">${proPeak.inputCacheHit}</td>
                    <td className="text-right px-4 py-3">${proPeak.output}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-slate-700 mb-4">
              Peak hours are <strong>01:00–04:00</strong> and <strong>06:00–10:00 UTC</strong>,{" "}
              <strong>Monday to Friday</strong> — 09:00–12:00 and 14:00–18:00 Beijing time. Every
              other hour is off-peak, weekends included. That is 35 of 168 hours, so a workload spread
              evenly across the week pays roughly 21% above the off-peak rate; batch work scheduled
              outside the windows pays nothing extra.
            </p>

            <Card className="mb-8 border-amber-300 bg-amber-50/60">
              <CardContent className="p-6 flex items-start gap-3">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-1 shrink-0" />
                <p className="text-sm text-slate-700 mb-0">
                  <strong>A correction we owe readers.</strong> Until this release our pricing page
                  described the peak-hour surcharge as announced but not active, because the official
                  rate card showed one flat tier. That is no longer true: it went live with this
                  release, and the surcharge is a straight <strong>100%</strong> — off-peak is exactly
                  half of peak. Every page on this site that quotes DeepSeek rates now reads from one
                  verified configuration, so the change propagated everywhere at once.
                </p>
              </CardContent>
            </Card>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              What this costs in practice
            </h2>
            <p className="text-slate-700 mb-4">
              A concrete example on V4.1-Flash off-peak: 1M input tokens on a cache miss plus 1M
              output tokens costs <strong>$0.75</strong>. Add a stable system prompt so 90% of input
              hits the cache and it falls to about <strong>$0.62</strong>. Run the identical call
              inside a peak window and it is <strong>$1.50</strong>. Caching and scheduling are now
              worth more than model choice for most bills.
            </p>
            <p className="text-slate-700 mb-8">
              Our{" "}
              <Link to="/pricing" className="text-[#0066FF] underline">
                DeepSeek pricing page
              </Link>{" "}
              carries the full table, the price history and a calculator with a peak-hour toggle, and
              the homepage comparator puts the same workload next to GPT, Claude and Gemini rates.
            </p>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Migration checklist
            </h2>
            <ul className="text-slate-700 space-y-2 list-disc pl-5 mb-8">
              <li>
                Switch model strings to <code>deepseek-flash</code>. The old Flash names still resolve,
                but relying on a retired alias is technical debt with a deadline.
              </li>
              <li>
                Drop <code>deepseek-v4-flash-vision-exp</code> and send images to{" "}
                <code>deepseek-flash</code> directly.
              </li>
              <li>
                If you are on <code>deepseek-v4-pro</code>, decide deliberately: accept the routing to
                V4.1-Flash, or contact DeepSeek to keep V4-Pro service and its billing.
              </li>
              <li>
                Re-check your cost model against the two-tier card, and move batch jobs out of the two
                peak windows.
              </li>
              <li>
                Re-run your own evaluations. Benchmarks reported by a model vendor are a starting
                point, not a verdict on your workload.
              </li>
            </ul>

            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mt-10 mb-4">
              Frequently asked questions
            </h2>
            <div className="space-y-5 mb-10">
              {faqSchema.mainEntity.map((q) => (
                <div key={q.name}>
                  <h3 className="font-semibold text-slate-900 mb-1 flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 text-[#0066FF] mt-1 shrink-0" />
                    {q.name}
                  </h3>
                  <p className="text-slate-700 text-sm pl-6">{q.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>

            <div className="rounded-lg border border-slate-200 bg-slate-50 p-5 mb-10 text-sm text-slate-600">
              <strong className="text-slate-900">Sources.</strong> DeepSeek's official release notes
              for DeepSeek-V4.1-Flash (deepseek.com, September 9, 2026) and DeepSeek's official API
              pricing page (api-docs.deepseek.com), read on September 18, 2026. This is an independent
              guide and is not affiliated with DeepSeek.
            </div>

            <div className="border-t border-slate-200 pt-8">
              <h2 className="text-xl font-bold text-slate-900 mb-4">Keep reading</h2>
              <RelatedLinks />
            </div>
          </article>
        </main>
      </div>
    </>
  );
};

export default DeepseekV41FlashGA;
